import { ResetIcon, UploadIcon } from '@radix-ui/react-icons';
import { Flex } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { DialogButton } from 'components/common/dialogs/button';
import { HINT_DELIMITER } from 'components/common/form/text-hint';
import { CommonTextareaInput } from 'components/common/form/textarea';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IMachine, IMachineHardwareConfig } from 'lib_ts/interfaces/i-machine';
import { useCallback, useState } from 'react';

const DEFAULT_CONFIG: IMachineHardwareConfig = {
  ball_screw_type: 'NA',
  computer_model: 'NA',
  hdpe_type: 'NA',
  inserter_guide: 'NA',
  ip_address: 'NA',
  mac_address: 'NA',
  manufacturer: 'NA',
  motor_controller: 'NA',
  position_controller: 'NA',
  spring_setup: 'NA',
  vention_box_location: 'NA',
  wheel_serial_w1: 'NA',
  wheel_serial_w2: 'NA',
  wheel_serial_w3: 'NA',
  wheel_type: 'NA',
  tv_id: 'NA',
};

interface IProps {
  machine: Partial<IMachine>;
}

export const HardwareConfigTab = ({ machine }: IProps) => {
  /** make a copy to manipulate via forms */
  const config = machine.hardware_config ?? DEFAULT_CONFIG;
  const updating = useMachinesPortalStore((s) => s.updating);
  const updateMachines = useMachinesPortalStore((s) => s.updateMachines);

  const [initialValue] = useState(config);
  const [json, setJson] = useState(
    JSON.stringify(
      {
        // auto-populates new required keys
        ...DEFAULT_CONFIG,
        // preserves existing values
        ...config,
      },
      null,
      2
    )
  );

  const getErrors = useCallback((): string[] | undefined => {
    try {
      if (!json) {
        return ['Value cannot be empty'];
      }

      const parsedConfig = JSON.parse(json) as IMachineHardwareConfig;
      if (!parsedConfig) {
        return ['Value cannot be parsed into JSON'];
      }

      const output: string[] = [];

      if (!parsedConfig.wheel_type) {
        output.push('"wheel_type" is required');
      }

      if (!parsedConfig.spring_setup) {
        output.push('"spring_setup" is required');
      }

      if (!parsedConfig.inserter_guide) {
        output.push('"inserter_guide" is required');
      }

      if (!parsedConfig.hdpe_type) {
        output.push('"hdpe_type" is required');
      }

      if (!parsedConfig.wheel_serial_w1) {
        output.push('"wheel_serial_w1" is required');
      }

      if (!parsedConfig.wheel_serial_w2) {
        output.push('"wheel_serial_w2" is required');
      }

      if (!parsedConfig.wheel_serial_w3) {
        output.push('"wheel_serial_w3" is required');
      }

      if (!parsedConfig.position_controller) {
        output.push('"position_controller" is required');
      }

      if (!parsedConfig.motor_controller) {
        output.push('"motor_controller" is required');
      }

      if (!parsedConfig.tv_id) {
        output.push('"tv_id" is required');
      }

      return output.length > 0 ? output : undefined;
    } catch (e) {
      console.error(e);
      return ['Invalid JSON (see console)'];
    }
  }, [json]);

  const handleUpdate = async () => {
    if (!json) {
      NotifyHelper.warning({ message_md: 'Update value cannot be empty.' });
      return;
    }

    const errors = getErrors();
    if (errors && errors.length > 0) {
      NotifyHelper.warning({ message_md: errors[0] });
      return;
    }

    try {
      await updateMachines([
        {
          _id: machine._id,
          hardware_config: JSON.parse(json),
        },
      ]);

      NotifyHelper.success({
        message_md: 'Hardware config updated successfully.',
      });
    } catch (error) {
      NotifyHelper.error({
        message_md: `Failed to update hardware config  ${error}`,
      });
    }
  };

  const handleReset = () => {
    setJson(JSON.stringify(initialValue, null, 2));
  };

  const isAirtable = !!machine.at_record_id_hw_config;

  return (
    <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
      <CommonTextareaInput
        id="hw-config-update"
        label="Update Value"
        name="config"
        rows={20}
        value={json}
        disabled={updating || isAirtable}
        onChange={(v) => setJson(v ?? '')}
        hint_md={
          isAirtable
            ? `These values should be edited via the Airtable record ${machine.at_record_id_hw_config}`
            : [
                'Some changes will only take effect after the machine is restarted (e.g. connections).',
                getErrors()?.join(HINT_DELIMITER),
              ].join('\n\n')
        }
        monospace
      />

      {!isAirtable && (
        <Flex gap={RADIX.FLEX.GAP.SM} justify="end">
          <DialogButton
            icon={<ResetIcon />}
            label="common.reset"
            tooltip="Reset to active config"
            color={RADIX.COLOR.WARNING}
            disabled={updating}
            onClick={handleReset}
          />
          <DialogButton
            icon={<UploadIcon />}
            label="common.update"
            disabled={updating}
            onClick={handleUpdate}
            variant="solid"
          />
        </Flex>
      )}
    </Flex>
  );
};
